export interface HelpTextConfig {
  title: string;
  content: string;
  examples?: {
    good?: string[];
    bad?: string[];
    plainText?: string[];
  };
  notes?: string[];
  sections?: {
    title: string;
    content?: string;
    items?: string[];
  }[];
}

export const toolFormHelpText: Record<string, HelpTextConfig> = {
  toolName: {
    title: "Tool Name",
    content: "Provide a clear, functional name that reflects what the tool does. The AI uses this name to determine relevance during execution.",
    notes: [
      "Use snake_case formatting.",
      "Focus on clarity and intent—make the function of the tool obvious.",
      "Avoid ambiguous terms or internal-only abbreviations.",
      "Steer clear of symbols, uppercase letters, or overly generic words."
    ],
    examples: {
      good: ["generate_invoice", "fetch_user_profile", "send_notification_email"],
      bad: ["GenerateInvoice", "getDetails", "tool_123"]
    }
  },

  toolDescription: {
    title: "Tool Description",
    content: "Summarize the tool’s behavior and purpose. This helps AI understand when and how to use it.",
    notes: [
      "Stick to the high-level functionality.",
      "Avoid code-level implementation details.",
      "Skip parameter or return value documentation here."
    ],
    examples: {
      good: [
        "Creates an invoice using order and billing information.",
        "Retrieves profile data for a given user identifier.",
        "Delivers an email notification to one or more recipients."
      ],
      bad: [
        "A tool that performs a task",
        "Fetch user",
        "return type is object"
      ]
    }
  },

  toolTags: {
    title: "Tool Tags",
    content: "Add searchable tags to group and filter tools more easily within the studio.",
    notes: [
      "Use specific terms that reflect the tool's domain or action.",
      "Include any applicable functional category or module.",
      "Don't use overly broad or unrelated tags."
    ]
  },

  toolUrlEndpoint: {
    title: "URL Endpoint",
    content: "Enter the public HTTPS endpoint that will be invoked when the tool runs. It must accept POST requests.",
    notes: [
      "Only HTTPS endpoints are supported.",
      "POST requests will carry the input data in the body.",
      "The tool should support a `test_mode=true` query param to allow safe test runs with mock behavior.",
      "Authentication is passed via the `authorization` header."
    ],
    examples: {
      good: [
        "https://api.platform.io/v1/invoice/generate",
        "https://services.internal.net/user/profile",
        "https://automation.app/tools/notify"
      ]
    }
  },

  toolAuthentication: {
    title: "Authentication",
    content: "Specify the bearer token to be included in the `authorization` header when calling this tool.",
    notes: [
      "Use a dedicated token for this integration—do not reuse shared secrets.",
      "If this header should not be sent, disable it explicitly.",
      "For cross-system calls, use token rotation or service-to-service authentication mechanisms."
    ]
  },

  toolTimeout: {
    title: "Timeout (seconds)",
    content: "Define how long the system should wait for a response before assuming the tool has failed."
  },

  toolInputParameters: {
    title: "Input Parameters",
    content: "Describe the inputs your tool expects. These will be provided in the POST body.",
    notes: [
      "Parameter names should clearly convey their purpose.",
      "Include optional values, data formats, and value constraints where relevant."
    ],
    sections: [
      {
        title: "Parameter Fields",
        items: [
          "Key - Name of the parameter (used in payload)",
          "Description - What the parameter is used for",
          "Type - Acceptable data type",
          "Options - (Optional) Predefined list of valid values or rules",
          "Example - (Optional) Typical or valid value"
        ]
      }
    ]
  },

  toolOutput: {
    title: "Output Format",
    content: "Define the structure and semantics of the expected output.",
    notes: [
      "Make the response structure predictable and consistent.",
      "Return helpful error messages for known failure conditions.",
      "Help AI differentiate between a failed process, invalid input, or a transient issue."
    ],
    sections: [
      {
        title: "Output Fields",
        items: [
          "Description – Purpose and content of the output",
          "Type – Format or datatype (e.g. JSON, string, object)"
        ]
      }
    ]
  },

  toolAnnotation: {
    title: "Annotation",
    content: "This is an auto-generated summary that captures what the tool does and how it’s used. AI uses this to reason about the tool.",
    notes: [
      "Complete all other fields before generating the annotation.",
      "Review the annotation output to ensure it reflects recent updates.",
      "You can manually adjust the generated content to clarify tool behavior or edge cases."
    ]
  }
};


export const agentFormHelpText: Record<string, HelpTextConfig> = {
  agentName: {
    title: "Agent Name",
    content: "Define a distinct and meaningful name that reflects the agent’s core responsibility. This helps AI interpret its purpose more effectively.",
    notes: [
      "Use CamelCase formatting for consistency.",
      "Aim for clarity over creativity—name should hint at the agent’s domain.",
      "Skip special characters, numbers, or vague labels.",
      "Avoid terms like 'test', 'demo', or 'generic'."
    ],
    examples: {
      good: [
        "DataSyncCoordinator",
        "AccessRequestHandler",
        "IncidentReviewAgent"
      ],
      bad: [
        "MyAgent",
        "Test2",
        "helper_bot"
      ]
    }
  },

  agentDescription: {
    title: "Agent Description",
    content: "Explain what the agent is designed to handle. This description guides the AI in determining when and how to delegate tasks to this agent.",
    notes: [
      "Focus on the agent’s functional role, not implementation specifics.",
      "Don’t list individual tools—the system already maps those automatically.",
      "Include triggers, boundaries, or conditions under which the agent should act."
    ],
    examples: {
      good: [
        "Handles incoming requests related to user access and identity validation. Should be invoked whenever user credential workflows or permission checks are required."
      ]
    }
  },

  agentTags: {
    title: "Tags",
    content: "Attach relevant labels to improve searchability and filtering within the platform.",
    notes: [
      "Use action-oriented or domain-specific terms.",
      "Include tags that reflect the agent's operational scope.",
      "Avoid overly broad or internal jargon."
    ]
  },

  agentPrompt: {
    title: "System Prompt",
    content: "Define the instructions that guide the agent’s behavior during execution. This acts as its internal operating context.",
    notes: [
      "Craft this prompt carefully to establish tone, scope, and decision-making guidelines.",
      "Avoid overloading the prompt with tool-specific knowledge—that's handled separately.",
      "Use this area for task-level strategy, fallback handling, and interaction policies."
    ]
  },

  agentSelectedTools: {
    title: "Assigned Tools",
    content: "Choose which tools this agent can access. The AI will use tool annotations to decide how and when to call them during a task."
  }
};


export const playbookFormHelpText: Record<string, HelpTextConfig> = {
  playbookName: {
    title: "Playbook Name",
    content: "Assign a clear and distinct name that reflects the workflow’s purpose. This label is used for referencing the playbook during execution but is not interpreted by AI.",
    notes: [
      "Use CamelCase formatting where appropriate.",
      "Choose concise names that indicate the objective or scope.",
      "Avoid symbols, ambiguous labels, or placeholder terms.",
      "Include relevant context where needed (e.g., function, process area)."
    ],
    examples: {
      good: [
        "SubscriptionUpdateFlow",
        "AccessReviewProcedure",
        "ServiceRestartSequence"
      ],
      bad: [
        "Renewal",
        "RestartPlaybook",
        "NewPkg"
      ]
    }
  },

  playbookProductName: {
    title: "Product Name",
    content: "Specify the associated product or system context. You may add a new one if not listed."
  },

  playbookDescription: {
    title: "Description",
    content: "Summarize the intent and scope of this playbook. This is for internal visibility and not processed by AI.",
    notes: [
      "Clarify the use case and scope of the workflow.",
      "Note known boundaries, assumptions, or preconditions.",
      "Mention any operational constraints or limitations."
    ],
    examples: {
      good: [
        "This workflow manages subscription-related change requests, including validations, approvals, and final updates. It does not support trial extension cases, which must be handled manually."
      ]
    }
  },

  playbookTags: {
    title: "Tags",
    content: "Provide labels that categorize the playbook logically within the workspace.",
    notes: [
      "Include domain-specific or action-oriented keywords.",
      "Add terms related to the associated systems or teams.",
      "Avoid overly generic or redundant tags."
    ]
  },

  playbookWorkflow: {
    title: "Workflow Steps",
    content: "Define the sequence of steps executed by the agent network. Each step should be actionable and clear.",
    notes: [
      "Use a structured, step-by-step format.",
      "Identify conditional branches, fallback paths, or exit criteria.",
      "For ambiguous tasks, describe validation or resolution logic.",
      "Where applicable, align the structure with existing system processes."
    ],
    examples: {
      plainText: [
        `1. Check if a reference ID is provided; if not, attempt to infer it from available user or system data.
2. Use the reference ID to retrieve associated records.
  - If the record is missing or invalid, notify the requester and exit.
3. Classify the request based on origin (internal vs. external).
  - Internal requests proceed directly to step 6.
  - External requests require validation.
4. Locate the responsible reviewer based on request context.
5. Check if prior approval exists. If not, send a request for confirmation.
6. If approval is received or not required, execute the action.
7. Inform the requester of the result.
8. Log completion and finalize the task.`
      ]
    }
  },

  playbookSpecialInstructions: {
    title: "Execution Notes",
    content: "Include any non-step-specific guidelines for how the workflow should behave.",
    notes: [
      "Focus on nuance, timing, or behavior—not on step logic.",
      "Keep instructions simple, direct, and relevant."
    ]
  },

  playbookApprovalNotes: {
    title: "Approval Logic",
    content: "Outline how approval is determined in this playbook. This will be enforced by the system when applicable.",
    notes: [
      "Approval criteria should also be reflected in the workflow itself.",
      "Avoid vague phrasing—use deterministic conditions."
    ],
    examples: {
      plainText: [
        `A request is approved if either:
  (A) It originates from a pre-verified internal source, or
  (B) An assigned reviewer has explicitly confirmed approval.`
      ]
    }
  },

  playbookActiveTools: {
    title: "Direct Tools",
    content: "Select tools available for direct use within this playbook. These tools do not require a specific agent to invoke them."
  },

  playbookHelperAgents: {
    title: "Supporting Agents",
    content: "Choose agents that can assist in carrying out this workflow. Each agent will operate using its own prompt and assigned tools."
  }
};


export const adapterFormHelpText: Record<string, HelpTextConfig> = {
  adapterName: {
    title: "Adapter Name",
    content: "Assign a unique identifier for the adapter. Use lowercase letters, numbers, and underscores only. This name determines the endpoint path.",
    notes: [
      "Avoid spaces, hyphens, and uppercase characters.",
      "Choose names that describe the adapter's purpose or integration point.",
      "Names should be URL-safe and easy to distinguish."
    ],
    examples: {
      good: ["event_listener", "json_transformer"],
      bad: ["My Adapter", "DATA_PROCESSOR"]
    }
  },

  adapterDescription: {
    title: "Adapter Description",
    content: "Briefly describe what this adapter is responsible for. This will help others understand its purpose and integration behavior.",
    notes: [
      "Keep the description short but meaningful.",
      "Mention any data sources, transformations, or external systems it touches."
    ],
    examples: {
      good: [
        "Converts payloads from webhook sources into platform-compatible format.",
        "Validates and reformats data before forwarding to internal services."
      ]
    }
  },

  adapterUrlEndpoint: {
    title: "URL Endpoint",
    content: "This is the endpoint through which the adapter receives input. It is automatically derived from the adapter’s name and does not need to be configured manually.",
    notes: [
      "The system will generate this based on the adapter name.",
      "The URL follows the format: https://<platform-domain>/adapter/{adapter_name}"
    ],
    examples: {
      good: ["https://studio.example.com/adapter/event_listener"]
    }
  },

  adapterAuthentication: {
    title: "Authentication",
    content: "Specify how incoming requests should be authenticated. Include any tokens or keys that need to be validated.",
    notes: [
      "Use secure tokens such as API keys or Bearer tokens.",
      "Do not share secrets across services unless necessary.",
      "Ensure the receiving service is configured to validate the credentials."
    ],
    examples: {
      good: ["Bearer token", "API key"]
    }
  },

  adapterTags: {
    title: "Tags",
    content: "Add one or more tags to organize and classify your adapter. Tags help with discovery and grouping in the platform.",
    notes: [
      "Use domain-specific or functional labels.",
      "Avoid overly broad terms unless widely accepted (e.g., 'integration')."
    ],
    examples: {
      good: ["webhook-handler", "data-ingestion", "api-integration"]
    }
  }
};


// Helper function to format help text content
export const formatHelpText = (config: HelpTextConfig): string => {
  let formattedText = config.content + "\n\n";

  if (config.notes?.length) {
    formattedText += "Notes:\n";
    config.notes.forEach(req => {
      formattedText += `• ${req}\n`;
    });
    formattedText += "\n";
  }

  if (config.examples) {
    // Show plainText examples if they exist
    if (config.examples.plainText?.length) {
      if (config.examples.plainText.length === 1) {
        formattedText += "Example:\n";
      } else {
        formattedText += "Examples:\n";
      }

      config.examples.plainText.forEach((example, index) => {
        // Add spacing between multiple plain text examples
        if (index > 0) formattedText += "\n";
        formattedText += example + "\n";
      });
      formattedText += "\n";
    }

    // Show good/bad examples if they exist
    if (config.examples.good?.length || config.examples.bad?.length) {
      // Add a separator if we had plainText examples
      if (config.examples.plainText?.length) {
        formattedText += "Additional examples:\n";
      } else {
        formattedText += "Examples:\n";
      }

      if (config.examples.good?.length) {
        config.examples.good.forEach(example => {
          formattedText += `✓ ${example}\n`;
        });
      }

      if (config.examples.bad?.length) {
        formattedText += "\nAvoid:\n";
        config.examples.bad.forEach(example => {
          formattedText += `✗ ${example}\n`;
        });
      }
      formattedText += "\n";
    }
  }

  if (config.sections?.length) {
    config.sections.forEach(section => {
      formattedText += `${section.title}:\n`;
      if (section.content) {
        formattedText += `${section.content}\n`;
      }
      if (section.items?.length) {
        section.items.forEach(item => {
          formattedText += ` • ${item}\n`;
        });
      }
    });
  }

  return formattedText.trim();
};